/**
 * נרמול מספרי טלפון ישראליים לפורמט E.164.
 *
 * מספר הטלפון משמש לזיהוי רשומת קבלן במאגר, ולכן חשוב שאותו מספר
 * שהוקלד בצורות שונות (050-1234567, 0501234567, +972 50 123 4567)
 * יגיע תמיד לאותו ערך. הנרמול אינו מוכיח בעלות על המספר.
 */

export type PhoneParseResult =
  | { ok: true; e164: string; national: string }
  | { ok: false; reason: 'empty' | 'invalid_chars' | 'invalid_length' | 'invalid_prefix' };

/** קידומות סלולר ישראליות (05X) וקידומות נייחות/VoIP נפוצות. */
const MOBILE_RE = /^5\d{8}$/;
const LANDLINE_RE = /^(2|3|4|8|9)\d{7}$/;
const VOIP_RE = /^7\d{8}$/;

export function parseIsraeliPhone(input: string): PhoneParseResult {
  const raw = (input ?? '').trim();
  if (!raw) return { ok: false, reason: 'empty' };

  // מותרים רק ספרות, רווחים, מקפים, סוגריים, נקודות ו-+ בהתחלה.
  if (!/^\+?[\d\s\-().]+$/.test(raw)) return { ok: false, reason: 'invalid_chars' };

  let digits = raw.replace(/\D/g, '');
  const hadPlus = raw.startsWith('+');

  if (hadPlus || digits.startsWith('972')) {
    if (!digits.startsWith('972')) return { ok: false, reason: 'invalid_prefix' };
    digits = digits.slice(3);
    // "+972 050..." — אפס מיותר אחרי קידומת המדינה
    if (digits.startsWith('0')) digits = digits.slice(1);
  } else if (digits.startsWith('00972')) {
    digits = digits.slice(5);
  } else if (digits.startsWith('0')) {
    digits = digits.slice(1);
  } else {
    return { ok: false, reason: 'invalid_prefix' };
  }

  if (digits.length < 8 || digits.length > 9) return { ok: false, reason: 'invalid_length' };
  if (!MOBILE_RE.test(digits) && !LANDLINE_RE.test(digits) && !VOIP_RE.test(digits)) {
    return { ok: false, reason: 'invalid_prefix' };
  }

  return { ok: true, e164: `+972${digits}`, national: formatNational(digits) };
}

/** מחזיר E.164 או null. */
export function normalizeIsraeliPhone(input: string): string | null {
  const result = parseIsraeliPhone(input);
  return result.ok ? result.e164 : null;
}

function formatNational(digits: string): string {
  if (digits.length === 9) return `0${digits.slice(0, 2)}-${digits.slice(2)}`;
  return `0${digits.slice(0, 1)}-${digits.slice(1)}`;
}

/** הצגה מקומית של מספר E.164 (‎+972501234567 → 050-1234567). */
export function formatPhoneForDisplay(e164: string): string {
  if (!e164.startsWith('+972')) return e164;
  return formatNational(e164.slice(4));
}

/** מיסוך מספר להצגה במקומות שבהם אין צורך במספר המלא (‎050-***4567). */
export function maskPhone(e164: string): string {
  const display = formatPhoneForDisplay(e164);
  const [prefix, rest] = display.split('-');
  if (!rest || rest.length < 4) return '***';
  return `${prefix}-${'*'.repeat(rest.length - 4)}${rest.slice(-4)}`;
}

export const PHONE_ERROR_MESSAGES: Record<Exclude<PhoneParseResult, { ok: true }>['reason'], string> = {
  empty: 'יש להזין מספר טלפון',
  invalid_chars: 'מספר הטלפון מכיל תווים לא חוקיים',
  invalid_length: 'מספר הטלפון קצר או ארוך מדי',
  invalid_prefix: 'יש להזין מספר טלפון ישראלי תקין',
};
